import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type {
  CreateInvestmentLabels,
  InvestmentDialogLabels,
} from "@/domain/types/investmentPiece";
import { useUploadDocumentLabels } from "./useUploadDocumentLabels";

export function useInvestmentDialogLabels(): InvestmentDialogLabels {
  const { t } = useTranslation();
  const upload = useUploadDocumentLabels();
  return useMemo(
    () => ({
      send: {
        titles: {
          reservation: t("investments.send_contract.title"),
          financing: t("investments.send_financing_contract.title"),
          communication: t("investments.send_communication_contract.title"),
        },
        description: t("investments.send_contract.confirmation_message"),
        template: t("investments.send_contract.fields.template"),
        templatePlaceholder: t("investments.send_contract.select_template"),
        paymentDeadline: t("investments.send_contract.fields.payment_deadline"),
        recipient: t("investments.send_contract.fields.recipient"),
        recipientHint: t("investments.send_contract.fields.recipient_hint"),
        message: t("investments.send_contract.fields.message"),
        messageHint: t("investments.send_contract.fields.message_hint"),
        messagePlaceholder: t("investments.send_contract.fields.message_placeholder"),
        messagePending: t("investments.send_contract.fields.message_pending"),
        preview: t("investments.send_contract.preview"),
        cancel: t("common.cancel"),
        send: t("investments.send_contract.send_button"),
        sending: t("investments.send_contract.sending"),
        summary: {
          investor: t("investments.reject.investor"),
          beneficiary: t("investments.reject.beneficiary"),
          quota: t("investments.fields.quota"),
          amount: t("investments.send_contract.info.investment"),
          returnPercentage: t("investments.send_contract.info.return_percentage"),
        },
      },
      extend: {
        title: t("investments.extend_reservation.title"),
        description: (investor: string) =>
          t("investments.extend_reservation.subtitle", { investor }),
        expiresOn: (date: string) =>
          t("investments.extend_reservation.current_expires_on", { date }),
        newDate: t("investments.extend_reservation.fields.new_expires_at"),
        cancel: t("common.cancel"),
        extend: t("investments.extend_reservation.extend_button"),
      },
      aeat: {
        title: t("investments.download_aeat.title"),
        description: (investor: string) => t("investments.download_aeat.subtitle", { investor }),
        unavailable: t("investments.download_aeat.fields.no_communication_contract"),
        documents: {
          communication: t("investments.download_aeat.fields.include_communication_contract"),
          financing: t("investments.download_aeat.fields.include_financing_contract"),
          certificate: t("investments.download_aeat.fields.include_certificate"),
        },
        cancel: t("common.cancel"),
        download: t("investments.download_aeat.download_button"),
      },
      upload: {
        title: t("investments.upload_tax_certificate.title"),
        description: t("investments.upload_tax_certificate.description"),
        dialog: upload,
      },
      uploadPayment: {
        title: t("investments.upload_payment_receipt.title"),
        description: t("investments.upload_payment_receipt.description"),
        dialog: upload,
      },
      resend: {
        title: (investor: string) => t("investments.resend.title", { investor }),
        description: t("investments.resend.description"),
        resend: t("investments.actions.resend"),
        success: t("investments.resend.success"),
      },
      reject: {
        title: (investor: string) => t("investments.reject.confirm_title", { investor }),
        description: (quota: string) => t("investments.reject.confirm_description", { quota }),
        reason: t("investments.reject.reason_label"),
        reasonPlaceholder: t("investments.reject.reason_placeholder"),
        cancel: t("investments.reject.cancel"),
        reject: t("investments.reject.confirm_button"),
      },
      remove: {
        title: (reference: string) =>
          t("investments.delete_investment.confirm_title", { reference }),
        description: t("investments.delete_investment.disclaimer_docs"),
        quotaReserved: t("investments.delete_investment.quota_reserved"),
        quotaCommitted: t("investments.delete_investment.quota_committed"),
        delete: t("investments.delete_investment.action_button"),
        success: t("investments.delete_investment.success_message"),
      },
      summary: {
        investor: t("investments.reject.investor"),
        beneficiary: t("investments.reject.beneficiary"),
        amount: t("investments.reject.investment"),
      },
    }),
    [t, upload],
  );
}

export function useInvestmentCreateLabels(): CreateInvestmentLabels {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      title: t("investments.new_investment"),
      description: t("investments.create.description"),
      beneficiary: t("investments.beneficiary"),
      beneficiaryPlaceholder: t("investments.create.beneficiary_placeholder"),
      beneficiarySearch: t("investments.create.search_beneficiary"),
      quota: t("investments.create.quota_label"),
      quotaPlaceholder: t("investments.select_fiscal_quota"),
      quotaNoBeneficiary: t("investments.create.quota_no_beneficiary"),
      quotaEmpty: t("investments.create.quota_empty"),
      investor: t("investments.investor"),
      investorPlaceholder: t("investments.create.investor_placeholder"),
      investorSearch: t("investments.create.search_investor"),
      amount: t("investments.create.amount"),
      available: (available: string, total: string) =>
        t("investments.create.available", { available, total }),
      exceeds: (available: string) => t("investments.create.exceeds", { available }),
      returnPercentage: t("investments.fields.return_percentage"),
      returnCap: t("investments.create.return_cap"),
      state: t("investments.status_label"),
      cancel: t("common.cancel"),
      create: t("investments.create.submit"),
      creating: t("investments.creating_investment"),
      meter: {
        label: t("investments.meter.label"),
        total: (quota: string) => t("investments.meter.total", { quota }),
        completed: t("investments.meter.completed"),
        reserved: t("investments.meter.reserved"),
        investment: t("investments.fields.new_investment"),
        transferredDeduction: t("investments.meter.transferred_deduction"),
        available: t("investments.fields.available"),
      },
    }),
    [t],
  );
}
